import { useTinyHost } from "@tiny/host-contract";
import React from "react";
import {
  SearchField,
  SelectField,
  Button,
  Typography,
  Chip,
} from "@tiny/components";
import useDebounce from "@tiny/components/src/hooks/useDebounce";
import TrashFull from "@tiny/icons/generated/TrashFull";
import BarLeft from "@tiny/icons/generated/BarLeft";
import FilterOutline from "@tiny/icons/generated/FilterOutline";
import UploadOutline from "@tiny/icons/generated/UploadOutline";
import ChipWithTooltip from "@tiny/components/src/ChipWithTooltip/ChipWithTooltip";
import { sortSubscribersOptions } from "../SubscribersList.helpers";
import { CustomerStatuses, CustomerStatus } from "../SubscribersList.types";
import { useStyles } from "./SubscribersListHeader.styles";
import ExportAudienceDialog from "../../ExportAudienceDialog";

interface SubscribersListHeaderProps {
  onDeleteClick: () => void;
  onDeselectAll: () => void;
  onExportClick: (type: string, columns?: string[]) => void;
  onSearchChange: (value: string) => void;
  onSortChange: (sort: string[]) => void;
  onColumnsSettingsOpen: () => void;
  onFilterOpen: () => void;
  onDeselectTag: (tag: string) => void;
  onDeselectStatus: (status: CustomerStatus) => void;
  selected: string[];
  sort: string[];
  tags: string[];
  statuses: CustomerStatuses;
  hideSort?: boolean;
  hideDelete?: boolean;
  customAttributeNames?: string[];
  showMove?: boolean;
  onMoveClick?: () => void;
  selectAllInAudience?: boolean;
  canSelectAllInAudience?: boolean;
  onSelectAllInAudience?: () => void;
  onClearSelection?: () => void;
  totalElements?: number;
}

function SubscribersListHeader(props: SubscribersListHeaderProps) {
  const {
    onDeleteClick,
    onDeselectAll,
    onExportClick,
    onSearchChange,
    onSortChange,
    onColumnsSettingsOpen,
    onFilterOpen,
    selected,
    sort,
    tags,
    statuses,
    onDeselectTag,
    onDeselectStatus,
    hideSort,
    hideDelete,
    customAttributeNames,
    showMove,
    onMoveClick,
    selectAllInAudience,
    canSelectAllInAudience,
    onSelectAllInAudience,
    onClearSelection,
    totalElements,
  } = props;

  const classes = useStyles();

  const [search, setSearch] = React.useState("");
  const [exportVisible, setExportVisible] = React.useState(false);

  const searchDebounced = useDebounce(search);

  const { capabilities } = useTinyHost();
  const actionDisabled = capabilities.actionsDisabled;

  const handleExport = (type: string, columns?: string[]) => {
    onExportClick(type, columns);
  };

  React.useEffect(() => {
    onSearchChange(searchDebounced);
  }, [onSearchChange, searchDebounced]);

  const shouldShowTagsAndStatuses = tags.length > 0 || statuses.length > 0;

  return (
    <>
      <div className={classes.root}>
        <div className={classes.wrap}>
          <div className={classes.top}>
            {selected.length > 0 || selectAllInAudience ? (
              <div className={classes.selected}>
                {showMove && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={onMoveClick}
                    className={classes.moveButton}
                  >
                    Move to audience
                  </Button>
                )}
                {!hideDelete && !selectAllInAudience && (
                  <Button
                    variant="outlined"
                    color="red"
                    endIcon={<TrashFull />}
                    onClick={onDeleteClick}
                    className={classes.deleteButton}
                  >
                    Delete
                  </Button>
                )}
                {selectAllInAudience ? (
                  <>
                    <Button onClick={onClearSelection} variant="text" noPadding>
                      Clear selection
                    </Button>
                    <Typography color="grey" tone={600} ml={5} variant="body2">
                      All {totalElements} contacts in this audience selected
                    </Typography>
                  </>
                ) : (
                  <>
                    <Button onClick={onDeselectAll} variant="text" noPadding>
                      Deselect all
                    </Button>
                    <Typography color="grey" tone={600} ml={5} variant="body2">
                      {selected.length} selected
                    </Typography>
                    {canSelectAllInAudience && (
                      <Button
                        onClick={onSelectAllInAudience}
                        variant="text"
                        noPadding
                      >
                        Select all {totalElements} in this audience
                      </Button>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className={classes.search}>
                <SearchField
                  value={search}
                  placeholder="Search"
                  onChangeValue={setSearch}
                  className={classes.searchField}
                />
                <Button
                  className={classes.button}
                  onClick={onFilterOpen}
                  variant="outlined"
                  endIcon={<FilterOutline />}
                >
                  Filter
                </Button>
                <Button
                  className={classes.button}
                  onClick={onColumnsSettingsOpen}
                  variant="outlined"
                  endIcon={<BarLeft />}
                >
                  Columns
                </Button>
              </div>
            )}
          </div>
          <div className={classes.bottom}>
            <div className={classes.actions}>
              <Button
                disabled={actionDisabled}
                variant="text"
                noPadding
                disabledTooltip="Upgrade to Export"
                onClick={() => setExportVisible(true)}
                endIcon={<UploadOutline />}
              >
                Export
              </Button>
            </div>
            {!hideSort && (
              <div className={classes.sort}>
                <SelectField
                  options={sortSubscribersOptions}
                  value={sort}
                  onChangeValue={onSortChange}
                />
              </div>
            )}
          </div>
        </div>
        {shouldShowTagsAndStatuses && (
          <div className={classes.tags}>
            {statuses.map((status) => (
              <Chip
                key={status}
                label={status}
                color="grey"
                onDelete={() => onDeselectStatus(status)}
              />
            ))}
            {tags.map((tag) => (
              <ChipWithTooltip
                key={tag}
                label={tag}
                color="grey"
                onDelete={() => onDeselectTag(tag)}
                maxVisibleChars={16}
              />
            ))}
          </div>
        )}
      </div>
      <ExportAudienceDialog
        onClose={() => setExportVisible(false)}
        onSuccess={handleExport}
        open={exportVisible}
        customAttributeNames={customAttributeNames}
      />
    </>
  );
}

export default React.memo(SubscribersListHeader);
